"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Whether the page's registered coach (SideNavSlots `coach`) is open, and
 * whether it fills the viewport. The page may own `open` itself (a result
 * page opens the chat from a one-click coaching action), in which case the
 * nav relays through its `onOpenChange`; otherwise the nav keeps it, closed
 * unless the page asked for `defaultOpen` (the home page). Below `md` there
 * is no rail to open beside, so open means fullscreen, and a default-open
 * chat stays closed rather than covering the page. Both reset when the route
 * changes, like the rest of the nav's transient state.
 *
 * Closing hands focus back to whatever opened the chat, when the chat had
 * it and the opener is still on the page (the mobile header's button; the
 * strip's Ask button is replaced while the chat is open, so there focus is
 * left to the browser).
 */
export function useNavCoach({ coach, hasRail, pathname }) {
  const [ownOpen, setOwnOpen] = useState(false);
  const [wantFullscreen, setWantFullscreen] = useState(false);
  const openerRef = useRef(null);
  const registered = Boolean(coach?.chat);
  const defaultOpen = registered && Boolean(coach.defaultOpen) && hasRail;
  const onOpenChange = coach?.onOpenChange;

  useEffect(() => {
    setOwnOpen(defaultOpen);
    setWantFullscreen(false);
  }, [pathname, defaultOpen]);

  const open = registered && Boolean(coach.open ?? ownOpen);
  const setOpen = useCallback(
    (next) => {
      if (next) openerRef.current = document.activeElement;
      onOpenChange?.(next);
      setOwnOpen(next);
      if (!next) setWantFullscreen(false);
    },
    [onOpenChange],
  );

  useEffect(() => {
    if (open) return;
    const opener = openerRef.current;
    openerRef.current = null;
    // Only when focus went down with the chat: closing because search
    // opened leaves focus in the search field.
    const active = document.activeElement;
    const focusLost = !active || active === document.body;
    if (focusLost && opener?.isConnected) opener.focus();
  }, [open]);

  return {
    registered,
    open,
    fullscreen: open && (wantFullscreen || !hasRail),
    setOpen,
    setFullscreen: setWantFullscreen,
  };
}
